import {store} from '../configureStore.js';
import isEmpty from 'lodash/isEmpty';
import {parseQueryParams} from './';
import {getUserEntitlementLevel} from '@adsk/ha-entitlements-util';
import worker from 'workerize-loader!../workers/worker'; // eslint-disable-line import/no-webpack-loader-syntax
import log from 'loglevel';

let workerInstance = worker(); // `new` is optional

/**
 * Checks if User is in homepage
 * see ld utils tests for all tested host names
 *
 * @returns {boolean}
 */
export const isUrlValid = (pageUrl) => {
  let isPageUrlValid = false;
  const reg =
    /(.*)autodesk.(com|[a-zA-Z]{0,3})|(\.[a-zA-Z]{0,2})\/*|\/[a-zA-Z]{0,2}\/?$/g;
  const validUrls = ['localhost', 'knowledge'];

  validUrls.forEach((url) => {
    if (pageUrl.includes(url)) {
      isPageUrlValid = true;
      return;
    }
  });

  if (isPageUrlValid) {
    return isPageUrlValid;
  }

  return typeof pageUrl === 'string' && pageUrl.replace(reg, '').length > 0;
};

/**
 * Gets entitlement level for ld user object
 *
 * @returns { string  or undefined }
 */
const getUserEntitlementLevelForLdUser = () => {
  const {userEntitlements} = store.getState().userData;

  return getUserEntitlementLevel(userEntitlements);
};

/**
 * builds ld user object
 *
 * @param {string} country
 * @param {string} language
 * @param {string} product
 * @param {string} origin
 * @param {string} pageUrl
 * @param {boolean} allowFeatureTestingParam
 * @param {boolean} allowFlagParam
 * @param {boolean} allowAnalyticsLogging
 * @param {boolean} allowImproveExperience
 * @param {string} analyticsId
 * @returns { Object }
 */
export const buildLdUserObject = ({
  country,
  language: local,
  product,
  origin,
  allowFeatureTestingParam,
  allowFlagParam,
  pageUrl,
  allowAnalyticsLogging,
  allowImproveExperience,
  integrationType,
  analyticsId,
  userId,
  isMobileDevice=false,
  userEntitlementsData = {},
  userCaseCount,
}) => {
  const {release} = store.getState().appSettings;
  const entitlement = getUserEntitlementLevelForLdUser();
  const isValidUrlForTesting = isUrlValid(pageUrl);
  const timezone = new Date().getTimezoneOffset() / -60;


  const userObject = {
    ...(country && {country}), //eg. US
    custom: {
      ...(entitlement && {entitlement}),
      ...(local && {local}), //eg. en_US
      ...(product && {product}),
      ...(origin && {origin}), // used for segmenting by site
      ...(integrationType && {integrationType}),
      ...(typeof allowFeatureTestingParam !== 'undefined' && {
        allowFeatureTestingParam, //?allowFeatureTesting=true for LD
        analyticsId, // temporary usage to limit access to Rosetta work
      }),
      ...(typeof allowFlagParam !== 'undefined' && {allowFlagParam}), //?allowFlag=true 2ndary LD flag
      allowAnalyticsLogging, //adskGdprAnalytics
      allowImproveExperience, //adskGdprImproveExp
      isExperimentUrl: isValidUrlForTesting,
      isHighestLevelBenefitFeatureDefined: userEntitlementsData.hasOwnProperty(
        'highestLevelSupportBenefitFeature'
      ),
      ...(userCaseCount >= 0 && {userCaseCount}),
      timezone: timezone,
      userId,
      pageUrl,
      isMobileDevice,
      release,
    }
  }

  log.debug('[LDuserobect][utilLaunchDarkly.js][]', userObject);


  return userObject
};

/**
 * Gets stored ld user object properties
 *
 * @returns {object}
 */
export const launchDarklyUserObject = () => {
  const storeState = store.getState();
  const {appSettings: siteData} = storeState;
  const analyticsId = storeState.userData.analyticsId;
  const userId = store.getState().userData.userDetails.userid;

  const {
    country,
    language,
    product,
    origin,
    pageUrl,
    allowAnalyticsLogging,
    allowImproveExperience,
    integrationType,
    isMobileDevice,
  } = siteData;

  const {
    allowFeatureTesting: allowFeatureTestingParam,
    allowFlag: allowFlagParam,
  } = getLdQueryParams();

  return buildLdUserObject({
    country,
    language,
    product,
    origin,
    allowFeatureTestingParam,
    allowFlagParam,
    pageUrl,
    allowAnalyticsLogging,
    allowImproveExperience,
    integrationType,
    analyticsId,
    userId,
    isMobileDevice,
  });
};

/**
 * initiates ld user update
 *
 * @returns {undefined}
 */
export const updateLdClient = () => {
  const {
    launchDarkly: {storeLdClient},
  } = store.getState();

  if (!isEmpty(storeLdClient)) {
    const currentLdUser = storeLdClient.getUser();
    const printUpdatedLdUser = () => {};

    const userObject = {
      ...currentLdUser,
      ...launchDarklyUserObject(),
    };

    storeLdClient.identify(userObject).then((userFlags) => {
      printUpdatedLdUser();
    });
  }
};

/**
 * Check is for entitlement level based user experience. It is not for identifying
 * individual users
 * @param {object} storedEntitlements
 * @param {object} oxygenEntitlements
 * @returns { boolean }
 */
export const shouldLdClientBeUpdated = (
  storedEntitlements,
  oxygenEntitlements
) => {
  return (
    storedEntitlements &&
    oxygenEntitlements &&
    getUserEntitlementLevel(storedEntitlements) !==
      getUserEntitlementLevel(oxygenEntitlements)
  );
};

/**
 * gets ld experiment name for analytics
 *
 * @returns { string } up to 100 chars if experiments otherwise control
 */
export const getLdUserExperimentName = () => {
  let experimentFlagNames = '';
  const {
    launchDarkly: {storeLdClient},
  } = store.getState();

  if (!isEmpty(storeLdClient)) {
    const userFlags = storeLdClient.allFlags() || {};
    for (const flagName in userFlags) {
      const flagValue = userFlags[flagName];
      let flagValueForADP = flagValue;
      //Check if the flag is a JSON flag. If yes, then get the expName for analyt
      if (
        flagValue &&
        typeof flagValue === 'object' &&
        flagValue.constructor === Object
      ) {
        const {expName = 'control'} = flagValue;
        flagValueForADP = expName;
      }
      if (flagName.startsWith('exp')) {
        experimentFlagNames += `${flagName}=${flagValueForADP},`;
      }
    }
  }

  return experimentFlagNames && experimentFlagNames.length > 0
    ? experimentFlagNames.replace(/,$/, '')
    : 'control';
};

/**
 * gets ld allow query params
 *
 * @returns { object }
 */
export const getLdQueryParams = () => {
  let allowFeatureTesting, allowFlag;

  const ldQueryParams = parseQueryParams(window.location.search, [
    'allowFeatureTesting',
    'allowFlag',
  ]);

  if (ldQueryParams.allowFeatureTesting) {
    allowFeatureTesting =
      ldQueryParams.allowFeatureTesting === 'true' ? true : false;
  }

  if (ldQueryParams.allowFlag) {
    allowFlag = ldQueryParams.allowFlag === 'true' ? true : false;
  }

  return {
    allowFeatureTesting,
    allowFlag,
  };
};

// map apptier to LD env
export const getLDEnvironment = (tier) => {
  switch (tier) {
    case 'dev':
      return 'development';
    case 'int':
      return 'qa';
    case 'stg':
      return 'staging';
    case 'prd':
    default:
      return 'production';
  }
};

export const getLDVariation = ({variation}) => {
  let result = {
    enable: false,
    hasVariation: false,
  };

  switch (variation.toUpperCase()) {
    case 'A':
      result = {
        enable: true,
        hasVariation: false,
      };
      break;
    case 'B':
      result = {
        enable: true,
        hasVariation: true,
      };
      break;
    case 'CONTROL':
      return result;
    default:
      break;
  }

  return result;
};
